import type { OfferVehicleDataView, OfferVehicleIdentifier } from "@slfinrtl/ui-bff-routes"
import {
	getVehicleDataByNewCarOrderIdForCsvOfferRoute,
	getVehicleDataByPorscheCodeForCsvOfferRoute,
	getVehicleDataForStockOfferRoute,
} from "@slfinrtl/ui-bff-routes"
import { useEffect, useState } from "react"
import { useBffDynamic } from "src/hooks"

interface UseOfferVehicleDataProps {
	vehicleIdentifier?: OfferVehicleIdentifier
}

interface OfferVehicleData {
	hasError: boolean
	view?: OfferVehicleDataView
}

export const useOfferVehicleData = ({ vehicleIdentifier }: UseOfferVehicleDataProps): OfferVehicleData => {
	const [view, setView] = useState<OfferVehicleDataView | undefined>()
	const [hasError, setHasError] = useState(false)

	const fetchStockVehicleData = useBffDynamic(getVehicleDataForStockOfferRoute, "inline")
	const fetchCsvVehicleData = useBffDynamic(getVehicleDataByPorscheCodeForCsvOfferRoute, "inline")
	const fetchNewCarOrderIdVehicleData = useBffDynamic(getVehicleDataByNewCarOrderIdForCsvOfferRoute, "inline")

	useEffect(() => {
		async function loadVehicleData() {
			if (!vehicleIdentifier) {
				return
			}

			setHasError(false)
			setView(undefined)

			const fetchVehicleData: () => Promise<OfferVehicleDataView> = (() => {
				switch (vehicleIdentifier.type) {
					case "INVENTORY_VEHICLE_ID":
						return () =>
							fetchStockVehicleData({
								body: undefined,
								pathParams: { vehicleId: vehicleIdentifier.vehicleId },
								queryParams: {},
							})
					case "PORSCHE_CODE":
						return () =>
							fetchCsvVehicleData({
								body: undefined,
								pathParams: { porscheCode: vehicleIdentifier.porscheCode },
								queryParams: {},
							})
					case "NEW_CAR_ORDER_ID":
						return () =>
							fetchNewCarOrderIdVehicleData({
								body: undefined,
								pathParams: { newCarOrderId: vehicleIdentifier.newCarOrderId },
								queryParams: {},
							})
				}
			})()

			try {
				const fetchedView = await fetchVehicleData()
				setView(fetchedView)
			} catch {
				setHasError(true)
			}
		}

		void loadVehicleData()
	}, [fetchCsvVehicleData, fetchNewCarOrderIdVehicleData, fetchStockVehicleData, vehicleIdentifier])

	return {
		hasError,
		view,
	}
}
